import React from "react";
import { fmtUSD } from "../../lib/api";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  CartesianGrid, Cell, PieChart, Pie, LineChart, Line, ComposedChart, Area,
} from "recharts";

const moneyTick = (v) => {
  const n = Math.abs(v);
  if (n >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(v / 1000).toFixed(1)}k`;
  return `$${Math.round(v)}`;
};

const fmtVal = (v, format) => {
  if (format === "hours") return `${Math.round(v).toLocaleString()} hr`;
  return fmtUSD(v, { compact: true });
};

const CHART_H = { sm: "h-44", md: "h-56", lg: "h-64" };
const tickStyle = { fontSize: 10, fontFamily: "IBM Plex Mono" };
const ttStyle = { borderRadius: 4, fontSize: 12 };

function KpiBody({ data }) {
  const up = data.delta_pct >= 0;
  return (
    <div className="pt-1">
      <div className="cs-mono text-2xl font-semibold text-foreground">
        {fmtUSD(data.value, { compact: true })}
      </div>
      <div className="flex items-center gap-2 mt-1.5">
        <span className={`cs-mono inline-flex items-center gap-0.5 text-xs font-semibold ${up ? "text-[#be185d]" : "text-[#15803d]"}`}>
          {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {Math.abs(data.delta_pct)}%
        </span>
        <span className="text-[11px] text-muted-foreground">{data.sublabel}</span>
      </div>
      <div className="text-[11px] text-muted-foreground mt-1">
        Previous: <span className="cs-mono">{fmtUSD(data.prev, { compact: true })}</span>
      </div>
    </div>
  );
}

function BarBody({ data, size }) {
  const horizontal = !!data.horizontal;
  return (
    <div className={CHART_H[size]}>
      <ResponsiveContainer>
        <BarChart data={data.data} layout={horizontal ? "vertical" : "horizontal"} margin={{ left: horizontal ? 8 : 0, right: 12, top: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={horizontal} horizontal={!horizontal} />
          {horizontal ? (
            <>
              <XAxis type="number" tick={tickStyle} stroke="#52525B" tickFormatter={(v) => fmtVal(v, data.format)} />
              <YAxis dataKey="name" type="category" tick={tickStyle} stroke="#52525B" width={110} />
            </>
          ) : (
            <>
              <XAxis dataKey="name" tick={tickStyle} stroke="#52525B" />
              <YAxis tick={tickStyle} stroke="#52525B" tickFormatter={(v) => (data.format === "hours" ? `${Math.round(v / 1000)}k` : moneyTick(v))} />
            </>
          )}
          <Tooltip contentStyle={ttStyle} formatter={(v) => fmtVal(v, data.format)} />
          <Bar dataKey="value" radius={horizontal ? [0, 3, 3, 0] : [3, 3, 0, 0]} maxBarSize={horizontal ? 16 : 40}>
            {data.data.map((d, i) => (
              <Cell key={i} fill={d.color || data.color || "#00579e"} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function StackedBody({ data, size }) {
  return (
    <div className={CHART_H[size]}>
      <ResponsiveContainer>
        <BarChart data={data.data} margin={{ left: 0, right: 12, top: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
          <XAxis dataKey={data.x_key} tick={tickStyle} stroke="#52525B" />
          <YAxis tick={tickStyle} stroke="#52525B" tickFormatter={moneyTick} />
          <Tooltip contentStyle={ttStyle} formatter={(v) => fmtUSD(v, { compact: true })} />
          <Legend wrapperStyle={{ fontSize: 10 }} iconSize={8} />
          {data.series.map((s) => (
            <Bar key={s.key} dataKey={s.key} stackId={data.grouped ? undefined : "s"} fill={s.color} maxBarSize={40} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function LineBody({ data, size, area = false }) {
  const Wrap = area ? ComposedChart : LineChart;
  return (
    <div className={CHART_H[size]}>
      <ResponsiveContainer>
        <Wrap data={data.data} margin={{ left: 0, right: 12, top: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
          <XAxis dataKey={data.x_key} tick={tickStyle} stroke="#52525B" interval={Math.max(1, Math.floor(data.data.length / 8))} />
          <YAxis tick={tickStyle} stroke="#52525B" tickFormatter={moneyTick} />
          <Tooltip contentStyle={ttStyle} formatter={(v) => fmtUSD(v, { compact: true })} />
          <Legend wrapperStyle={{ fontSize: 10 }} iconSize={8} />
          {data.series.map((s) =>
            area ? (
              <Area key={s.key} type="monotone" dataKey={s.key} stackId="a" stroke={s.color} fill={s.color} fillOpacity={0.25} strokeWidth={1.5} />
            ) : (
              <Line key={s.key} type="monotone" dataKey={s.key} stroke={s.color} strokeWidth={1.5} dot={false} />
            )
          )}
        </Wrap>
      </ResponsiveContainer>
    </div>
  );
}

function DonutBody({ data, size }) {
  return (
    <>
      <div className={size === "sm" ? "h-36" : "h-44"}>
        <ResponsiveContainer>
          <PieChart>
            <Pie data={data.data} dataKey="value" nameKey="name" innerRadius={38} outerRadius={62} paddingAngle={2}>
              {data.data.map((d, i) => (
                <Cell key={i} fill={d.color} />
              ))}
            </Pie>
            <Tooltip contentStyle={ttStyle} formatter={(v) => fmtUSD(v, { compact: true })} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="space-y-1 mt-1">
        {data.data.map((p) => (
          <div key={p.name} className="flex items-center gap-2 text-xs">
            <span className="w-2 h-2 rounded-sm" style={{ background: p.color }} />
            <span className="text-muted-foreground flex-1">{p.name}</span>
            <span className="cs-mono font-medium">{fmtUSD(p.value, { compact: true })}</span>
          </div>
        ))}
      </div>
    </>
  );
}

function ComboBody({ data, size }) {
  return (
    <div className={CHART_H[size]}>
      <ResponsiveContainer>
        <ComposedChart data={data.data} margin={{ left: 0, right: 12, top: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
          <XAxis dataKey={data.x_key} tick={tickStyle} stroke="#52525B" interval={Math.max(1, Math.floor(data.data.length / 7))} />
          <YAxis yAxisId="left" tick={tickStyle} stroke="#52525B" tickFormatter={(v) => `${Math.round(v / 1000)}k hr`} />
          <YAxis yAxisId="right" orientation="right" tick={tickStyle} stroke={data.line.color} tickFormatter={moneyTick} />
          <Tooltip contentStyle={ttStyle} />
          <Legend wrapperStyle={{ fontSize: 10 }} iconSize={8} />
          <Area yAxisId="left" type="monotone" dataKey={data.area.key} name={data.area.label} fill={data.area.color} fillOpacity={0.18} stroke={data.area.color} strokeWidth={1.5} />
          <Line yAxisId="right" type="monotone" dataKey={data.line.key} name={data.line.label} stroke={data.line.color} strokeWidth={2} dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function TableBody({ data }) {
  const cellFmt = (row, col) => {
    const v = row[col.key];
    if (col.format === "usd") return <span className="cs-mono font-semibold text-[#00579e]">{fmtUSD(v, { compact: true })}</span>;
    if (col.format === "usd_green") return <span className="cs-mono font-semibold text-[#15803d]">{fmtUSD(v, { compact: true })}</span>;
    if (col.format === "pct_delta") {
      const up = v >= 0;
      return (
        <span className={`cs-mono inline-flex items-center gap-0.5 text-xs ${up ? "text-[#be185d]" : "text-[#15803d]"}`}>
          {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {Math.abs(v)}%
        </span>
      );
    }
    return v;
  };
  return (
    <div className="overflow-x-auto -mx-5 -mb-5 mt-2">
      <table className="w-full text-sm">
        <thead className="bg-[hsl(var(--cs-tint))] border-y border-border">
          <tr className="text-left">
            {data.columns.map((c) => (
              <th key={c.key} className={`px-4 py-2 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground ${c.align === "right" ? "text-right" : ""}`}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row, i) => (
            <tr key={i} className="cs-row-hover border-b border-border last:border-0">
              {data.columns.map((c) => (
                <td key={c.key} className={`px-4 py-2 ${c.align === "right" ? "text-right" : ""} ${!c.format ? "text-xs text-muted-foreground" : ""}`}>
                  {cellFmt(row, c)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function EconWidget({ def, data, size = "md" }) {
  if (!data) {
    return <div className="text-xs text-muted-foreground py-6 text-center">No data for this widget.</div>;
  }
  switch (data.kind) {
    case "kpi": return <KpiBody data={data} />;
    case "bar": return <BarBody data={data} size={size} />;
    case "bar_stacked": return <StackedBody data={data} size={size} />;
    case "line_multi": return <LineBody data={data} size={size} />;
    case "area_multi": return <LineBody data={data} size={size} area />;
    case "donut": return <DonutBody data={data} size={size} />;
    case "combo": return <ComboBody data={data} size={size} />;
    case "table": return <TableBody data={data} />;
    default: return null;
  }
}
